/**
 * This util function will take in an array of objects that contain the same property name and sort in ascending/descending order based on that property.
 * If the object does not contain the property name, these objects will not be sorted and will be appended to end of the list.
 *
 * @param {array} array - array to be sorted
 * @param {string} firstProperty - object property name to sort by
 * @param {string} secondProperty - object property to do a secondary sort
 * @param {string} [order='descending'] - order by ascending or descending
 * @return returns one of the arrays below:
 *         - sorted array in ascending/descending order with any unsorted objects at the end of the list
 *         - the original array if the specified property cannot be found for any object
 */
export const sortByObjectProperty = (array, firstProperty, secondProperty, order = 'descending') => {
  const newArray = JSON.parse(JSON.stringify(array));
  const containsPropertyArray = [];
  const noPropertyArray = [];
  for (let i = 0; i < newArray.length; i++) {
    if (firstProperty in newArray[i]) {
      containsPropertyArray.push(newArray[i]);
    } else {
      noPropertyArray.push(newArray[i]);
    }
  }

  const sortedArray = containsPropertyArray.sort(function (a, b) {
    if (secondProperty) {
      if (a[firstProperty] === b[firstProperty]) {
        if (order === 'ascending') return a[secondProperty] > b[secondProperty] ? 1 : -1;
        if (order === 'descending') return a[secondProperty] < b[secondProperty] ? 1 : -1;
      }
    }
    if (order === 'ascending') return a[firstProperty] > b[firstProperty] ? 1 : -1;
    return a[firstProperty] < b[firstProperty] ? 1 : -1;
  });

  return sortedArray.concat(noPropertyArray);
};

/**
 * This util function will take in an array of objects that contain the same property name and sort in descending order based on that property.
 * If the two values are the same, it will do a secondary sort with the for the second property based on a priority value
 * If the object does not contain the property name, these objects will not be sorted and will be appended to end of the list.
 *
 * @param {array} array array to be sorted
 * @param {string} propertyName property name to be sorted by
 * @param {string} secondPropertyName second property name to compare priority
 * @param {string} secondPriorityValue priority value which is compared to second property
 * @returns returns one of the arrays below:
 *         - sorted array in descending order with secondary priority with any unsorted objects at the end of the list
 *         - the original array if the specified property cannot be found for any object
 */
export const sortByObjectPropertyWithPriority = (array, propertyName, secondPropertyName, secondPriorityValue) => {
  const newArray = JSON.parse(JSON.stringify(array));
  const containsPropertyArray = [];
  const noPropertyArray = [];
  for (let i = 0; i < newArray.length; i++) {
    if (propertyName in newArray[i]) {
      containsPropertyArray.push(newArray[i]);
    } else {
      noPropertyArray.push(newArray[i]);
    }
  }
  const sortedArray = containsPropertyArray.sort(function (a, b) {
    if (a[propertyName] === b[propertyName]) {
      return a[secondPropertyName] === secondPriorityValue ? -1 : 1;
    }
    return a[propertyName] < b[propertyName] ? 1 : -1;
  });
  return sortedArray.concat(noPropertyArray);
};

/**
 * Takes in two arrays of objects that contain the 'propertyToSortWith' property
 * Sorts unsortedList to match with sortedList order based on property
 * @param {*} unsortedList List to be sorted (contains objects with property 'propertyToSortWith')
 * @param {*} sortedList Correctly sorted list to compare to (contains objects with property 'propertyToSortWith')
 * @param {*} propertyToSortWith property in the object to sort with
 * @returns sorted list or the original list if the correct property is not given
 */
export const sortListBasedOnAnotherList = (unsortedList, sortedList, propertyToSortWith) => {
  return unsortedList.sort((a, b) => {
    return (
      sortedList.findIndex((p) => {
        if (p[propertyToSortWith] && a[propertyToSortWith]) {
          return p[propertyToSortWith].toString() === a[propertyToSortWith].toString();
        }
        return null;
      }) -
      sortedList.findIndex((p) => {
        if (p[propertyToSortWith] && b[propertyToSortWith]) {
          return p[propertyToSortWith].toString() === b[propertyToSortWith].toString();
        }
        return null;
      })
    );
  });
};
